
import { useEffect, useRef, useState } from 'react'
import { Server, Code, Network, Bot, Layers, ChevronLeft, ChevronRight } from 'lucide-react'
import { CardContent, Card } from './Card'

const services = [
  {
    icon: Layers,
    title: 'Data Architecture',
    description: 'Secure foundations that make your data available wherever it\'s needed',
  },
  {
    icon: Code,
    title: 'Full-Stack Development',
    description: 'Custom applications built with modern technologies and practices',
  },
  {
    icon: Bot,
    title: 'Workflow Automation',
    description: 'Automations and AI agents built to save time and reduce manual work',
  },
  {
    icon: Network,
    title: 'Systems Integration',
    description: 'Seamless integration of diverse systems for enhanced operational efficiency',
  },
  {
    icon: Server,
    title: 'Service Maintenance',
    description: 'Maintenance and optimization of IT services and infrastructure',
  },
]

const AUTOPLAY_INTERVAL_MS = 3500
const AUTOPLAY_RESUME_DELAY_MS = 3000
const DRAG_FRICTION = 0.6
const DRAG_SWIPE_THRESHOLD = 0.2
const SHOW_ARROWS = true

// Distinct scroll positions the carousel can rest at. On mobile there is one per card;
// on desktop several cards are visible at once, so the last few share the end position.
const getSlidePositions = (container, cards) => {
  if (!container) return [0]
  const maxScroll = container.scrollWidth - container.clientWidth
  const paddingLeft = parseFloat(getComputedStyle(container).paddingLeft) || 0
  const positions = []
  cards.forEach((card) => {
    if (!card) return
    const left = Math.min(maxScroll, Math.max(0, card.offsetLeft - paddingLeft))
    if (!positions.length || left - positions[positions.length - 1] > 1) positions.push(left)
  })
  return positions.length ? positions : [0]
}

const scrollToSlide = (container, cards, index) => {
  const positions = getSlidePositions(container, cards)
  if (!container || positions[index] === undefined) return
  container.scrollTo({ left: positions[index], behavior: 'smooth' })
}

const CarouselArrow = ({ direction, disabled, onClick }) => {
  const Icon = direction < 0 ? ChevronLeft : ChevronRight
  return (
    <button
      aria-label={direction < 0 ? 'Previous services' : 'Next services'}
      disabled={disabled}
      onClick={onClick}
      className={`hidden lg:flex h-6 w-6 items-center justify-center transition-colors ${
        disabled ? 'text-primary/20 cursor-default' : 'text-primary/60 hover:text-primary'
      }`}
    >
      <Icon className="h-4 w-4" />
    </button>
  )
}

const App = () => {
  const carouselRef = useRef(null)
  const cardRefs = useRef([])
  const [activeSlide, setActiveSlide] = useState(0)
  const [slideCount, setSlideCount] = useState(services.length)
  const activeSlideRef = useRef(0)
  const isPausedRef = useRef(false)
  const resumeTimeoutRef = useRef(null)

  useEffect(() => {
    const container = carouselRef.current
    if (!container) return
    const update = () => {
      const positions = getSlidePositions(carouselRef.current, cardRefs.current)
      let nearest = 0
      positions.forEach((left, i) => {
        if (Math.abs(left - container.scrollLeft) < Math.abs(positions[nearest] - container.scrollLeft)) nearest = i
      })
      setSlideCount(positions.length)
      setActiveSlide(nearest)
      activeSlideRef.current = nearest
    }
    update()
    container.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      container.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const interval = setInterval(() => {
      const container = carouselRef.current
      if (!container || isPausedRef.current) return
      const count = getSlidePositions(carouselRef.current, cardRefs.current).length
      if (count <= 1) return
      scrollToSlide(container, cardRefs.current, (activeSlideRef.current + 1) % count)
    }, AUTOPLAY_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  const pauseAutoplay = () => {
    isPausedRef.current = true
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current)
  }

  const resumeAutoplaySoon = () => {
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current)
    resumeTimeoutRef.current = setTimeout(() => {
      isPausedRef.current = false
    }, AUTOPLAY_RESUME_DELAY_MS)
  }

  const dragStateRef = useRef({ dragging: false, startX: 0, startScrollLeft: 0, startIndex: 0 })

  useEffect(() => {
    const container = carouselRef.current
    if (!container) return

    const handleMouseMove = (e) => {
      const drag = dragStateRef.current
      if (!drag.dragging) return
      container.scrollLeft = drag.startScrollLeft - (e.clientX - drag.startX) * DRAG_FRICTION
    }

    const endDrag = (e) => {
      const drag = dragStateRef.current
      if (!drag.dragging) return
      dragStateRef.current.dragging = false
      container.style.scrollBehavior = ''
      container.style.scrollSnapType = ''
      const deltaX = e.clientX - drag.startX
      const cardWidth = cardRefs.current[0]?.offsetWidth || container.clientWidth
      let targetIndex = drag.startIndex
      if (Math.abs(deltaX) > cardWidth * DRAG_SWIPE_THRESHOLD) {
        targetIndex += deltaX < 0 ? 1 : -1
      }
      targetIndex = Math.max(0, Math.min(getSlidePositions(carouselRef.current, cardRefs.current).length - 1, targetIndex))
      scrollToSlide(container, cardRefs.current, targetIndex)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', endDrag)
    }

    const handleMouseDown = (e) => {
      e.preventDefault()
      dragStateRef.current = {
        dragging: true,
        startX: e.clientX,
        startScrollLeft: container.scrollLeft,
        startIndex: activeSlideRef.current,
      }
      container.style.scrollBehavior = 'auto'
      container.style.scrollSnapType = 'none'
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', endDrag)
    }

    const handlePauseStart = () => pauseAutoplay()
    const handlePauseEnd = () => resumeAutoplaySoon()

    container.addEventListener('mousedown', handleMouseDown)
    container.addEventListener('pointerdown', handlePauseStart)
    container.addEventListener('pointerup', handlePauseEnd)
    container.addEventListener('pointercancel', handlePauseEnd)
    container.addEventListener('mouseenter', handlePauseStart)
    container.addEventListener('mouseleave', handlePauseEnd)
    return () => {
      container.removeEventListener('mousedown', handleMouseDown)
      container.removeEventListener('pointerdown', handlePauseStart)
      container.removeEventListener('pointerup', handlePauseEnd)
      container.removeEventListener('pointercancel', handlePauseEnd)
      container.removeEventListener('mouseenter', handlePauseStart)
      container.removeEventListener('mouseleave', handlePauseEnd)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', endDrag)
    }
  }, [])

  const stepSlide = (direction) => {
    pauseAutoplay()
    scrollToSlide(carouselRef.current, cardRefs.current, activeSlideRef.current + direction)
    resumeAutoplaySoon()
  }

  return (
    <div className="App flex flex-col items-center">
      <figure className="max-w-4xl flex bg-white rounded-xl flex-col items-center relative">
        <div className="flex-auto text-left space-y-5 md:p-6 lg:p-6 lg:pb-2 flex flex-col mt-4 lg:mt-0" style={{ width: 540, maxWidth: '90vw' }}>
          <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" width="100%" zoomAndPan="magnify" viewBox="0 0 375 374.999991" height="100%" preserveAspectRatio="xMidYMid meet" version="1.0"><defs><g/><clipPath id="4cca2febb1"><path d="M 171.730469 40.613281 L 277.480469 40.613281 L 277.480469 146.363281 L 171.730469 146.363281 Z M 171.730469 40.613281 " clip-rule="nonzero"/></clipPath><clipPath id="c0680961df"><path d="M 171.730469 93.3125 L 224.230469 93.3125 L 224.230469 145.8125 L 171.730469 145.8125 Z M 171.730469 93.3125 " clip-rule="nonzero"/></clipPath><clipPath id="e4c3b077ef"><path d="M 119.03125 93.511719 L 171.53125 93.511719 L 171.53125 146.011719 L 119.03125 146.011719 Z M 119.03125 93.511719 " clip-rule="nonzero"/></clipPath><clipPath id="0829378f0a"><path d="M 171.929688 227.933594 L 278.429688 227.933594 L 278.429688 334.433594 L 171.929688 334.433594 Z M 171.929688 227.933594 " clip-rule="nonzero"/></clipPath><clipPath id="bc2251e4ca"><path d="M 171.160156 226.878906 L 252.160156 226.878906 L 252.160156 307.878906 L 171.160156 307.878906 Z M 171.160156 226.878906 " clip-rule="nonzero"/></clipPath><clipPath id="b9f6bffe6d"><path d="M 91.480469 227.769531 L 171.730469 227.769531 L 171.730469 308.019531 L 91.480469 308.019531 Z M 91.480469 227.769531 " clip-rule="nonzero"/></clipPath><clipPath id="92646b8ae1"><path d="M 171.941406 227.582031 L 224.441406 227.582031 L 224.441406 280.082031 L 171.941406 280.082031 Z M 171.941406 227.582031 " clip-rule="nonzero"/></clipPath></defs><rect x="-37.5" width="450" fill="#ffffff" y="-37.499999" height="449.999989" fill-opacity="1"/><rect x="-37.5" width="450" fill="#ffffff" y="-37.499999" height="449.999989" fill-opacity="1"/><g fill="#3e5e6b" fill-opacity="1"><g transform="translate(2.298036, 206.266926)"><g><path d="M 40.015625 0 L 27.5 0 L 15.265625 -18.234375 L 15.265625 0 L 4.953125 0 L 4.953125 -51.96875 L 15.265625 -51.96875 L 15.265625 -21.203125 L 26.71875 -38.171875 L 38.609375 -38.171875 L 25.734375 -20.015625 Z M 40.015625 0 "/></g></g></g><g fill="#3e5e6b" fill-opacity="1"><g transform="translate(46.548852, 206.266926)"><g><path d="M 22.40625 1.0625 C 18.832031 1.0625 15.6875 0.34375 12.96875 -1.09375 C 10.257812 -2.53125 8.160156 -4.53125 6.671875 -7.09375 C 5.191406 -9.664062 4.453125 -12.675781 4.453125 -16.125 L 4.453125 -38.171875 L 14.78125 -38.171875 L 14.78125 -17.328125 C 14.78125 -14.585938 15.453125 -12.453125 16.796875 -10.921875 C 18.140625 -9.390625 20.007812 -8.625 22.40625 -8.625 C 24.8125 -8.625 26.675781 -9.398438 28 -10.953125 C 29.320312 -12.515625 29.984375 -14.640625 29.984375 -17.328125 L 29.984375 -38.171875 L 40.296875 -38.171875 L 40.296875 -16.125 C 40.296875 -12.675781 39.550781 -9.664062 38.0625 -7.09375 C 36.582031 -4.53125 34.5 -2.53125 31.8125 -1.09375 C 29.125 0.34375 25.988281 1.0625 22.40625 1.0625 Z M 22.40625 1.0625 "/></g></g></g><g fill="#3e5e6b" fill-opacity="1"><g transform="translate(95.889049, 206.266926)"><g><path d="M 22.40625 1.0625 C 18.832031 1.0625 15.6875 0.34375 12.96875 -1.09375 C 10.257812 -2.53125 8.160156 -4.53125 6.671875 -7.09375 C 5.191406 -9.664062 4.453125 -12.675781 4.453125 -16.125 L 4.453125 -38.171875 L 14.78125 -38.171875 L 14.78125 -17.328125 C 14.78125 -14.585938 15.453125 -12.453125 16.796875 -10.921875 C 18.140625 -9.390625 20.007812 -8.625 22.40625 -8.625 C 24.8125 -8.625 26.675781 -9.398438 28 -10.953125 C 29.320312 -12.515625 29.984375 -14.640625 29.984375 -17.328125 L 29.984375 -38.171875 L 40.296875 -38.171875 L 40.296875 -16.125 C 40.296875 -12.675781 39.550781 -9.664062 38.0625 -7.09375 C 36.582031 -4.53125 34.5 -2.53125 31.8125 -1.09375 C 29.125 0.34375 25.988281 1.0625 22.40625 1.0625 Z M 22.40625 1.0625 "/></g></g></g><g fill="#3e5e6b" fill-opacity="1"><g transform="translate(145.229245, 206.266926)"><g><path d="M 36.125 -38.25 L 43.828125 -38.25 L 43.828125 -0.078125 L 35.703125 -0.078125 L 34.859375 -3.46875 C 33.203125 -2.050781 31.328125 -0.941406 29.234375 -0.140625 C 27.140625 0.660156 24.890625 1.0625 22.484375 1.0625 C 18.660156 1.0625 15.238281 0.179688 12.21875 -1.578125 C 9.207031 -3.347656 6.828125 -5.753906 5.078125 -8.796875 C 3.335938 -11.835938 2.46875 -15.289062 2.46875 -19.15625 C 2.46875 -22.976562 3.335938 -26.410156 5.078125 -29.453125 C 6.828125 -32.492188 9.207031 -34.882812 12.21875 -36.625 C 15.238281 -38.363281 18.660156 -39.234375 22.484375 -39.234375 C 24.929688 -39.234375 27.210938 -38.820312 29.328125 -38 C 31.453125 -37.175781 33.34375 -36.035156 35 -34.578125 Z M 23.328125 -8.703125 C 25.304688 -8.703125 27.082031 -9.148438 28.65625 -10.046875 C 30.238281 -10.941406 31.476562 -12.175781 32.375 -13.75 C 33.269531 -15.332031 33.71875 -17.132812 33.71875 -19.15625 C 33.71875 -21.132812 33.269531 -22.910156 32.375 -24.484375 C 31.476562 -26.066406 30.238281 -27.316406 28.65625 -28.234375 C 27.082031 -29.160156 25.304688 -29.625 23.328125 -29.625 C 21.304688 -29.625 19.515625 -29.160156 17.953125 -28.234375 C 16.398438 -27.316406 15.175781 -26.066406 14.28125 -24.484375 C 13.382812 -22.910156 12.9375 -21.132812 12.9375 -19.15625 C 12.9375 -17.132812 13.382812 -15.34375 14.28125 -13.78125 C 15.175781 -12.226562 16.398438 -10.992188 17.953125 -10.078125 C 19.515625 -9.160156 21.304688 -8.703125 23.328125 -8.703125 Z M 23.328125 -8.703125 "/></g></g></g><g fill="#3e5e6b" fill-opacity="1"><g transform="translate(198.59855, 206.266926)"><g><path d="M 18.53125 1.0625 C 15.320312 1.0625 12.515625 0.5 10.109375 -0.625 C 7.703125 -1.757812 5.828125 -3.351562 4.484375 -5.40625 C 3.140625 -7.457031 2.46875 -9.847656 2.46875 -12.578125 L 12.515625 -12.578125 C 12.554688 -10.890625 13.164062 -9.617188 14.34375 -8.765625 C 15.53125 -7.921875 17.039062 -7.5 18.875 -7.5 C 20.332031 -7.5 21.601562 -7.816406 22.6875 -8.453125 C 23.78125 -9.085938 24.328125 -10.039062 24.328125 -11.3125 C 24.328125 -12.632812 23.609375 -13.566406 22.171875 -14.109375 C 20.734375 -14.648438 19 -15.082031 16.96875 -15.40625 C 15.457031 -15.644531 13.890625 -15.976562 12.265625 -16.40625 C 10.640625 -16.832031 9.144531 -17.441406 7.78125 -18.234375 C 6.414062 -19.035156 5.296875 -20.144531 4.421875 -21.5625 C 3.546875 -22.976562 3.109375 -24.816406 3.109375 -27.078125 C 3.109375 -29.484375 3.753906 -31.601562 5.046875 -33.4375 C 6.347656 -35.28125 8.140625 -36.707031 10.421875 -37.71875 C 12.710938 -38.726562 15.414062 -39.234375 18.53125 -39.234375 C 21.59375 -39.234375 24.265625 -38.703125 26.546875 -37.640625 C 28.835938 -36.585938 30.617188 -35.101562 31.890625 -33.1875 C 33.160156 -31.28125 33.796875 -29.054688 33.796875 -26.515625 L 23.96875 -26.515625 C 23.96875 -28.023438 23.457031 -29.15625 22.4375 -29.90625 C 21.425781 -30.65625 20.023438 -31.03125 18.234375 -31.03125 C 16.640625 -31.03125 15.394531 -30.6875 14.5 -30 C 13.601562 -29.320312 13.15625 -28.441406 13.15625 -27.359375 C 13.15625 -26.085938 13.859375 -25.210938 15.265625 -24.734375 C 16.679688 -24.265625 18.378906 -23.867188 20.359375 -23.546875 C 21.921875 -23.265625 23.523438 -22.921875 25.171875 -22.515625 C 26.816406 -22.117188 28.347656 -21.519531 29.765625 -20.71875 C 31.179688 -19.914062 32.320312 -18.804688 33.1875 -17.390625 C 34.0625 -15.972656 34.5 -14.113281 34.5 -11.8125 C 34.5 -9.21875 33.835938 -6.953125 32.515625 -5.015625 C 31.203125 -3.085938 29.34375 -1.59375 26.9375 -0.53125 C 24.53125 0.53125 21.726562 1.0625 18.53125 1.0625 Z M 18.53125 1.0625 "/></g></g></g><g clip-path="url(#4cca2febb1)"><path fill="#a9d2ce" d="M 171.730469 40.613281 L 171.730469 146.011719 L 277.128906 146.011719 C 277.128906 87.800781 229.941406 40.613281 171.730469 40.613281 Z M 171.730469 40.613281 " fill-opacity="1" fill-rule="nonzero"/></g><g clip-path="url(#c0680961df)"><path fill="#3e5e6b" d="M 171.730469 93.3125 L 171.730469 146.011719 L 224.429688 146.011719 C 224.429688 116.90625 200.835938 93.3125 171.730469 93.3125 Z M 171.730469 93.3125 " fill-opacity="1" fill-rule="nonzero"/></g><g clip-path="url(#e4c3b077ef)"><path fill="#f4b89e" d="M 119.03125 146.011719 L 171.730469 146.011719 L 171.730469 93.3125 C 142.625 93.3125 119.03125 116.90625 119.03125 146.011719 Z M 119.03125 146.011719 " fill-opacity="1" fill-rule="nonzero"/></g><g clip-path="url(#0829378f0a)"><path fill="#f4b89e" d="M 278.429688 227.933594 L 171.976562 227.933594 L 171.976562 334.386719 C 230.769531 334.386719 278.429688 286.726562 278.429688 227.933594 Z M 278.429688 227.933594 " fill-opacity="1" fill-rule="nonzero"/></g><g clip-path="url(#bc2251e4ca)"><path fill="#ffffff" d="M 252.160156 226.878906 L 171.019531 226.878906 L 171.019531 308.019531 C 215.832031 308.019531 252.160156 271.691406 252.160156 226.878906 Z M 252.160156 226.878906 " fill-opacity="1" fill-rule="nonzero"/></g><g clip-path="url(#b9f6bffe6d)"><path fill="#a4d0cf" d="M 171.726562 308.019531 L 171.730469 308.019531 L 171.730469 227.933594 L 91.644531 227.933594 C 91.644531 272.164062 127.5 308.019531 171.726562 308.019531 Z M 171.726562 308.019531 " fill-opacity="1" fill-rule="nonzero"/></g><g fill="#3e5e6b" fill-opacity="1"><g transform="translate(279.431852, 206.266926)"><g><path d="M 4.234375 0 L 4.234375 -11.03125 L 15.203125 -11.03125 L 15.203125 0 Z M 4.234375 0 "/></g></g></g><g fill="#3e5e6b" fill-opacity="1"><g transform="translate(303.399432, 206.266926)"><g><path d="M 5.796875 -44.828125 L 5.796875 -51.6875 L 12.296875 -51.6875 L 12.296875 -44.828125 Z M 6.078125 0 L 6.078125 -38.171875 L 12.09375 -38.171875 L 12.09375 0 Z M 6.078125 0 "/></g></g></g><g fill="#3e5e6b" fill-opacity="1"><g transform="translate(326.09107, 206.266926)"><g><path d="M 23.265625 0.84375 C 19.398438 0.84375 15.945312 -0.0234375 12.90625 -1.765625 C 9.863281 -3.515625 7.457031 -5.882812 5.6875 -8.875 C 3.925781 -11.863281 3.046875 -15.269531 3.046875 -19.09375 C 3.046875 -22.90625 3.925781 -26.316406 5.6875 -29.328125 C 7.457031 -32.347656 9.863281 -34.71875 12.90625 -36.4375 C 15.945312 -38.164062 19.398438 -39.03125 23.265625 -39.03125 C 27.128906 -39.03125 30.59375 -38.15625 33.65625 -36.40625 C 36.71875 -34.664062 39.128906 -32.296875 40.890625 -29.296875 C 42.660156 -26.304688 43.546875 -22.90625 43.546875 -19.09375 C 43.546875 -15.269531 42.660156 -11.863281 40.890625 -8.875 C 39.128906 -5.882812 36.71875 -3.515625 33.65625 -1.765625 C 30.59375 -0.0234375 27.128906 0.84375 23.265625 0.84375 Z M 23.265625 -4.953125 C 25.992188 -4.953125 28.414062 -5.5625 30.53125 -6.78125 C 32.65625 -8.007812 34.332031 -9.679688 35.5625 -11.796875 C 36.789062 -13.921875 37.40625 -16.351562 37.40625 -19.09375 C 37.40625 -21.820312 36.789062 -24.242188 35.5625 -26.359375 C 34.332031 -28.484375 32.65625 -30.160156 30.53125 -31.390625 C 28.414062 -32.617188 25.992188 -33.234375 23.265625 -33.234375 C 20.523438 -33.234375 18.09375 -32.617188 15.96875 -31.390625 C 13.851562 -30.160156 12.191406 -28.484375 10.984375 -26.359375 C 9.785156 -24.242188 9.1875 -21.820312 9.1875 -19.09375 C 9.1875 -16.351562 9.785156 -13.921875 10.984375 -11.796875 C 12.191406 -9.679688 13.867188 -8.007812 16.015625 -6.78125 C 18.160156 -5.5625 20.578125 -4.953125 23.265625 -4.953125 Z M 23.265625 -4.953125 "/></g></g></g><g clip-path="url(#92646b8ae1)"><path fill="#3e5e6b" d="M 224.441406 227.582031 L 171.742188 227.582031 L 171.742188 280.28125 C 200.847656 280.28125 224.441406 256.683594 224.441406 227.582031 Z M 224.441406 227.582031 " fill-opacity="1" fill-rule="nonzero"/></g></svg>
        </div>
      </figure>
      {/*
      <div className="flex justify-center">
        <div className="align-center fixed bottom-10 right-10 lg:hidden">
          <a href="mailto:tuukka@kuuas.io" title="tuukka@kuuas.io">
            <button className="border-white hover:bg-secondary border-2 bg-primary text-white text-base font-medium px-5 py-5 rounded-full flex">
              <Mail className="h-6 w-6" />
            </button>
          </a>
        </div>
      </div>
      */}
      <div className="mx-3 flex flex-col items-center services flex-1 justify-center lg:flex-none lg:self-stretch lg:mt-1 lg:mb-auto">
        <div className="contents lg:relative lg:flex lg:w-full lg:max-w-[76rem]">
        <div
          ref={carouselRef}
          className="relative mt-4 lg:mt-0 items-center lg:items-start flex overflow-x-auto overscroll-x-contain snap-x snap-mandatory scroll-smooth scrollbar-hide gap-6 lg:gap-8 px-[7.5%] w-full pb-2 cursor-grab active:cursor-grabbing select-none lg:max-w-[76rem] lg:px-12 lg:scroll-px-12 carousel-fade services"
        >
          {services.map(({ icon: Icon, title, description }, index) => (
            <Card
              key={title}
              ref={(el) => (cardRefs.current[index] = el)}
              className="w-full shrink-0 snap-center lg:w-[min(22rem,calc((100%_-_4rem)/3))] lg:snap-start"
            >
              <CardContent className="flex flex-col items-center space-y-4 p-6">
                <Icon className="h-12 w-12" />
                <h3 className="text-lg text-xl font-bold mt-4 text-center [text-wrap:balance]">{title}</h3>
                <p className="max-w-md text-sm text-center mt-4 w-screen px-12 mx-[calc(50%-50vw)] lg:w-full lg:px-0 lg:mx-0 [text-wrap:balance]">{description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        </div>
        <div className="flex items-center justify-center gap-2 mt-2">
          {SHOW_ARROWS && (
            <CarouselArrow
              direction={-1}
              disabled={activeSlide === 0}
              onClick={() => stepSlide(-1)}
            />
          )}
          {Array.from({ length: slideCount }, (_, index) => (
            <button
              key={index}
              aria-label={`Go to slide ${index + 1}`}
              onClick={() => scrollToSlide(carouselRef.current, cardRefs.current, index)}
              className={`h-2 w-2 rounded-full transition-colors ${
                activeSlide === index ? 'bg-primary/80' : 'bg-primary/20'
              }`}
            />
          ))}
          {SHOW_ARROWS && (
            <CarouselArrow
              direction={1}
              disabled={activeSlide === slideCount - 1}
              onClick={() => stepSlide(1)}
            />
          )}
        </div>
      </div>
      <section className="w-full mt-16 py-16 lg:py-20 bg-secondary/[.15] flex justify-center">
        <p className="mx-6 text-center font-display italic text-2xl leading-snug text-[#2e4651] [text-wrap:balance] cta">
          <span className="block">Have a project in mind?</span>
          <span className="block mt-3">
            <a
              href="mailto:tuukka@kuuas.io"
              className="underline decoration-1 underline-offset-4 decoration-primary/30 hover:decoration-primary transition-colors"
            >
              Get in touch.
            </a>
          </span>
        </p>
      </section>
      <footer className="w-full py-6 text-white bg-primary flex flex-col justify-center items-center">
        <div className="container px-4 lg:px-6 flex justify-center">
          <div className="max-w-5xl mx-4 flex-1 flex flex-col items-center justify-between gap-4 lg:flex-row">
            <div className="flex flex-col items-center gap-4 px-8 lg:items-start lg:gap-2">
              <p className="text-center text-sm leading-loose lg:text-left">
                <div>Kuuas.io</div>
                <div>Viipurinkatu 12 L 271, 00510 Helsinki, Finland</div>
                <div>tuukka@kuuas.io</div>
                <div>FI29487297</div>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default App;
